import { Box, Dialog, Grid, LinearProgress, Typography } from "@mui/material";
import { useCallback, useEffect, useRef, useState } from "react";
import { Trans, useTranslation } from "react-i18next";

const operationLabelKeyMap = {
	add: "companion_operation_add",
	remove: "companion_operation_remove",
	refresh: "companion_operation_refresh",
};

const operationTimeMap = {
	setup: 10,
	add: 8,
	remove: 5,
	refresh: 5,
};

function getCompanionMarketLabelKey({
	hasCargosForTrans,
	hasCargosForTimocom,
}) {
	if (hasCargosForTrans && hasCargosForTimocom) {
		return "companion_market_exchanges";
	}

	if (hasCargosForTrans) {
		return "companion_market_trans";
	}

	if (hasCargosForTimocom) {
		return "companion_market_timocom";
	}

	return "";
}

export function useCompanionInfoDialog() {
	const { t } = useTranslation();
	const [open, setOpen] = useState(false);
	const [operationType, setOperationType] = useState("");
	const [offersCount, setOffersCount] = useState(0);
	const [marketLabelKey, setMarketLabelKey] = useState("");
	const [progress, setProgress] = useState(100);
	const resolverRef = useRef();
	const timeoutRef = useRef();
	const intervalRef = useRef();

	const clearTimers = useCallback(() => {
		if (timeoutRef.current) {
			globalThis.clearTimeout(timeoutRef.current);
			timeoutRef.current = undefined;
		}

		if (intervalRef.current) {
			globalThis.clearInterval(intervalRef.current);
			intervalRef.current = undefined;
		}
	}, []);

	const handleClose = useCallback(() => {
		clearTimers();

		setOpen(false);
		setProgress(100);
		resolverRef.current = undefined;
	}, [clearTimers]);

	const confirm = useCallback(
		({
			type,
			total,
			timeout = 5500,
			hasCargosForTrans,
			hasCargosForTimocom,
		}) => {
			if (resolverRef.current) {
				resolverRef.current(true);
			}

			clearTimers();

			setOperationType(type);
			setOffersCount(total);
			setMarketLabelKey(
				getCompanionMarketLabelKey({
					hasCargosForTrans,
					hasCargosForTimocom,
				}),
			);
			setProgress(100);
			setOpen(true);

			return new Promise((resolve) => {
				const startTime = Date.now();
				resolverRef.current = resolve;

				intervalRef.current = globalThis.setInterval(() => {
					const elapsed = Date.now() - startTime;
					const nextProgress = Math.max(0, 100 - (elapsed / timeout) * 100);
					setProgress(nextProgress);
				}, 100);

				timeoutRef.current = globalThis.setTimeout(() => {
					setProgress(0);
					resolve(true);
					handleClose();
				}, timeout);
			});
		},
		[clearTimers, handleClose],
	);

	useEffect(() => {
		return () => {
			clearTimers();

			if (resolverRef.current) {
				resolverRef.current(true);
			}
		};
	}, [clearTimers]);

	// Setup time + operation time per offer, converted to minutes and rounded up
	const estimatedTime = Math.ceil(
		(offersCount * (operationTimeMap[operationType] ?? 0)) / 60 +
			operationTimeMap["setup"] / 60,
	);
	const operationLabelKey = operationLabelKeyMap[operationType];
	const warningTranslationKey = marketLabelKey
		? "companion_info_warning"
		: "companion_info_warning_generic";

	const dialog = (
		<Dialog open={open} maxWidth="sm" fullWidth disableEscapeKeyDown>
			<Box sx={{ padding: "30px", pb: 0 }}>
				<Grid container rowSpacing={3} columnSpacing={2}>
					<Grid size={12}>
						<Box sx={{ textAlign: "center" }}>
							<Typography variant="h5" component="span">
								<Trans
									i18nKey="companion_info_summary"
									values={{
										operation: operationLabelKey ? t(operationLabelKey) : "",
										count: offersCount,
										estimatedTime,
									}}
									components={{ bold: <b /> }}
								/>
							</Typography>
						</Box>
						<Box sx={{ textAlign: "center", px: 5, mt: 1 }}>
							<Typography variant="h6" component="span">
								<Trans
									i18nKey={warningTranslationKey}
									values={{
										marketLabel: marketLabelKey ? t(marketLabelKey) : "",
									}}
								/>
							</Typography>
						</Box>
					</Grid>
				</Grid>
				<LinearProgress
					variant="determinate"
					value={progress}
					sx={{ mt: 3.5, mx: -3.75, height: 6 }}
				/>
			</Box>
		</Dialog>
	);

	return { confirm, dialog };
}
